import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { JurisdictionSelectionComponent } from '../jurisdiction-selection/jurisdiction-selection.component';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { ValidationProblem } from '../../interfaces/validation/validation-problem';
import { Address } from '../../interfaces/address/address';
import { ContactDetailsUIConfiguration } from '../../interfaces/ui-configurations/contact-details-ui-configuration';
import { TranslocoService } from '@jsverse/transloco';

export interface AddressFormLabels {
  streetNumber: string;
  streetName: string;
  unit: string;
  quadrant: string;
  city: string;
  postalCode: string;
  country: string;
  jurisdiction: string;
  search: string;
  noMatchesFound: string;
}

@Component({
  selector: 'lib-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddressFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() address: Address | undefined;
  @Input() uiConfiguration: ContactDetailsUIConfiguration | null | undefined;
  private _formLabel?: string;
  @Input() set formLabel(value: string | null | undefined) { this._formLabel = value ?? undefined; }
  get formLabel(): string { return this._formLabel ?? this.transloco.translate('common.labels.address'); }
  @Input() showFormLabel: boolean = true;
  @Input() staticJurisdiction?: string;
  @Input() staticCountryCode?: string;
  private _labels?: Partial<AddressFormLabels>;
  @Input() set labels(value: Partial<AddressFormLabels> | null | undefined) { this._labels = value ?? undefined; }
  /** Caller-provided labels override the translated defaults. */
  get labels(): AddressFormLabels {
    const defaults: AddressFormLabels = {
      streetNumber: this.transloco.translate('common.labels.streetNumber'),
      streetName: this.transloco.translate('common.labels.streetName'),
      unit: this.transloco.translate('common.labels.unit'),
      quadrant: this.transloco.translate('addressForm.quadrant'),
      city: this.transloco.translate('common.labels.city'),
      postalCode: this.transloco.translate('common.labels.postalCode'),
      country: this.transloco.translate('common.labels.country'),
      jurisdiction: this.transloco.translate('common.labels.jurisdiction'),
      search: this.transloco.translate('addressForm.search'),
      noMatchesFound: this.transloco.translate('addressForm.noMatchesFound'),
    };
    return { ...defaults, ...this._labels } as AddressFormLabels;
  }
  @Input() labelTranslator?: (code: string, type: 'country' | 'jurisdiction', countryCode?: string) => string;
  @Input() langChange$?: Observable<string>;
  @ViewChild(JurisdictionSelectionComponent) jurisdictionSelectionComponent!: JurisdictionSelectionComponent;

  @Output() public countryChosenEvent = new EventEmitter<string>();

  public addressDetails!: FormGroup;

  protected quadrants: string[] = [];

  streetNumberError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  streetNameError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  unitError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  quadrantError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  cityError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  postalCodeError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);

  private onDestroy$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder) { }

  initForm() {
    this.addressDetails = this.formBuilder.group({
      streetNumber: [this.address?.streetNumber],
      streetName: [this.address?.streetName],
      unit: [this.address?.unit],
      quadrant: [this.address?.quadrant],
      city: [this.address?.city],
      postalCode: [this.address?.postalCode]
    });
  }

  initJurisdictionsForm() {
    this.jurisdictionSelectionComponent.countryChosenEvent.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(country => this.countryChosenEvent.emit(country));

    // Use the fixed internal control names from jurisdiction-selection (not the translated labels)
    let countryControl = this.jurisdictionSelectionComponent.countrySelectionForm.controls['Country'];
    let jurisdictionControl = this.jurisdictionSelectionComponent.countrySelectionForm.controls['Jurisdiction'];

    this.addressDetails.setControl('countryCode', countryControl, { emitEvent: false });
    this.addressDetails.setControl('jurisdiction', jurisdictionControl, { emitEvent: false });

    if (this.address?.countryCode) {
      this.jurisdictionSelectionComponent.setCountrySelection(this.address.countryCode);
    } else if (this.uiConfiguration?.defaultCountry) {
      this.jurisdictionSelectionComponent.setCountrySelection(this.uiConfiguration.defaultCountry);
    }

    if (this.address?.jurisdiction) {
      this.jurisdictionSelectionComponent.setJurisdictionSelection(this.address.jurisdiction);
    }

    // Likely want to put this in a configuration eventually but not necessary now since it's only
    // being used for land title searches
    if (this.uiConfiguration?.showQuadrant) {
      if (this.uiConfiguration.showFrenchQuadrantValues) {
        this.quadrants = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
      } else {
        this.quadrants = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      }
    }
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngAfterViewInit(): void {
    this.initJurisdictionsForm();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  setCountrySelection(country: string) {
    this.jurisdictionSelectionComponent.setCountrySelection(country);
  }

  setJurisdictionSelection(jurisdiction: string) {
    this.jurisdictionSelectionComponent.setJurisdictionSelection(jurisdiction);
  }

  pushErrors(errors: ValidationProblem[] | undefined) {
    if (!errors) {
      return;
    }

    this.jurisdictionSelectionComponent.pushErrors(errors);

    this.streetNumberError$.next(errors?.filter(error => error.path.includes('/streetnumber')).at(0)?.userFriendlyMessage);
    this.streetNameError$.next(errors?.filter(error => error.path.includes('/streetname')).at(0)?.userFriendlyMessage);
    this.unitError$.next(errors?.filter(error => error.path.includes('/unit')).at(0)?.userFriendlyMessage);
    this.quadrantError$.next(errors?.filter(error => error.path.includes('/quadrant')).at(0)?.userFriendlyMessage);
    this.cityError$.next(errors?.filter(error => error.path.includes('/city')).at(0)?.userFriendlyMessage);
    this.postalCodeError$.next(errors?.filter(error => error.path.includes('/postalcode')).at(0)?.userFriendlyMessage);
  }

  clearQuadrant() {
    this.addressDetails.get('quadrant')?.setValue(null, { emitEvent: false });
  }
}
